/**
 * Collection-specific payload rules applied before submitting to the
 * Content Manager API (flattening nested objects, normalizing empty dates).
 * Mutates the passed object in place.
 */

import { API_CONFIG } from '../config/api.config';

const E = API_CONFIG.CONTENT_MANAGER_ENDPOINTS;

const nullEmptyDates = (obj: any, fields: string[]) => {
  fields.forEach((f) => {
    if (obj[f] === '') obj[f] = null;
  });
};

const flattenCurrency = (obj: any, field: string) => {
  if (obj[field] && typeof obj[field] === 'object') {
    obj[`${field}USD`] = obj[field].usd;
    obj[`${field}EGP`] = obj[field].egp;
    delete obj[field];
  }
};

export const applyCollectionRules = (cleaned: any, collectionType: string): void => {
  if (collectionType === E.DEBIT_NOTES) {
    ['amount', 'deduction', 'paid', 'netRemaining'].forEach(f => flattenCurrency(cleaned, f));
  }

  // Flatten crew evaluation nested period
  if (collectionType === E.CREW_EVALUATIONS) {
    if (cleaned.evaluationPeriod && typeof cleaned.evaluationPeriod === 'object') {
      cleaned.evaluationPeriodFrom = cleaned.evaluationPeriod.fromDate;
      cleaned.evaluationPeriodTo = cleaned.evaluationPeriod.toDate;
      delete cleaned.evaluationPeriod;
    }
  }

  // Flatten payment order authorizedBy
  if (collectionType === E.PAYMENT_ORDERS) {
    if (cleaned.authorizedBy && typeof cleaned.authorizedBy === 'object') {
      cleaned.authorizedBySignature = cleaned.authorizedBy.signature;
      cleaned.authorizedByName = cleaned.authorizedBy.name;
      cleaned.authorizedByJobTitle = cleaned.authorizedBy.jobTitle;
      delete cleaned.authorizedBy;
    }
  }

  // Flatten invoice hire nested objects
  if (collectionType === E.INVOICE_HIRES) {
    // Items are persisted separately as invoice-hire-item records
    delete cleaned.items;
    if (cleaned.financeManager && typeof cleaned.financeManager === 'object') {
      cleaned.financeManagerName = cleaned.financeManager.name;
      cleaned.financeManagerSignature = cleaned.financeManager.signature;
      delete cleaned.financeManager;
    }
    if (cleaned.generalManager && typeof cleaned.generalManager === 'object') {
      cleaned.generalManagerName = cleaned.generalManager.name;
      cleaned.generalManagerSignature = cleaned.generalManager.signature;
      delete cleaned.generalManager;
    }
    if (cleaned.socialInsurance && typeof cleaned.socialInsurance === 'object') {
      cleaned.insuranceStatus = cleaned.socialInsurance.insuranceStatus;
      cleaned.clientDeliveryDate = cleaned.socialInsurance.clientDeliveryDate;
      cleaned.insuranceDeliveryDate = cleaned.socialInsurance.insuranceDeliveryDate;
      cleaned.certificateNumber = cleaned.socialInsurance.certificateNumber;
      cleaned.certificateValue = cleaned.socialInsurance.certificateValue;
      cleaned.clientReceiptDate = cleaned.socialInsurance.clientReceiptDate;
      delete cleaned.socialInsurance;
    }
    nullEmptyDates(cleaned, ['clientDeliveryDate', 'insuranceDeliveryDate', 'clientReceiptDate', 'invoiceDate', 'operationDate']);
  }

  // Purchases: items live in the separate purchase-item collection.
  // Only the purchaseItems relation (array of documentIds) is sent on the purchase.
  if (collectionType === E.PURCHASES) {
    delete cleaned.items;
    if (cleaned.purchaseItems !== undefined) {
      const pi = cleaned.purchaseItems;
      if (!Array.isArray(pi) || (pi.length > 0 && typeof pi[0] !== 'string')) {
        delete cleaned.purchaseItems;
      }
    }
    ['orderDate', 'deliveryDate'].forEach((f) => {
      if (cleaned[f] === '' || cleaned[f] === undefined) cleaned[f] = null;
    });
    if (cleaned.exchangeRate === undefined || cleaned.exchangeRate === '') {
      cleaned.exchangeRate = null;
    }
  }

  // Requisitions & delivery notes: Strapi rejects empty strings for date fields
  if (collectionType === E.REQUISITIONS) {
    nullEmptyDates(cleaned, ['requestDate', 'requiredDeliveryDate', 'dateOfIssue', 'dateOfRevision', 'purchasedDate']);
  }
  if (collectionType === E.DELIVERY_NOTES) {
    nullEmptyDates(cleaned, ['date', 'deliveryDate', 'receivedDate']);
    // A populated requisition relation may slip through on updates
    if (cleaned.requisition && typeof cleaned.requisition === 'object') {
      cleaned.requisition = cleaned.requisition.documentId || cleaned.requisition.id || null;
    }
    // Backend stores the signature blocks as flat fields
    if (cleaned.deliveredBy && typeof cleaned.deliveredBy === 'object') {
      cleaned.deliveredByName = cleaned.deliveredBy.name;
      cleaned.deliveredBySignature = cleaned.deliveredBy.signature;
      delete cleaned.deliveredBy;
    }
    if (cleaned.deliveredTo && typeof cleaned.deliveredTo === 'object') {
      cleaned.deliveredToName = cleaned.deliveredTo.name;
      cleaned.deliveredToSignature = cleaned.deliveredTo.signature;
      cleaned.deliveredToZoom = cleaned.deliveredTo.zoom;
      delete cleaned.deliveredTo;
    }
  }

  // Products: Strapi rejects empty strings for date fields
  if (collectionType === E.PRODUCTS) {
    if (cleaned.lastRestockDate === '' || cleaned.lastRestockDate === undefined) {
      cleaned.lastRestockDate = null;
    }
  }

  // Permits: link the car/location relations the form tracks as *Id fields.
  // Populated relation objects that slip through on updates are normalized
  // back to their documentId.
  if (collectionType === E.CAR_PERMITS || collectionType === E.PERSONNEL_PERMITS) {
    if (cleaned.carId !== undefined) {
      cleaned.car = cleaned.carId === '' ? null : cleaned.carId;
      delete cleaned.carId;
    }
    if (cleaned.locationId !== undefined) {
      cleaned.location = cleaned.locationId === '' ? null : cleaned.locationId;
      delete cleaned.locationId;
    }
    ['car', 'location', 'employee', 'branch'].forEach((f) => {
      if (cleaned[f] && typeof cleaned[f] === 'object') {
        cleaned[f] = cleaned[f].documentId || cleaned[f].id || null;
      }
    });
    nullEmptyDates(cleaned, ['issueDate', 'expiryDate']);
  }

  // Document collections: forms submit empty strings for unset dates
  if (collectionType === E.CREW_CERTIFICATES ||
      collectionType === E.EMPLOYEE_DOCUMENTS ||
      collectionType === E.CAR_DOCUMENTS) {
    nullEmptyDates(cleaned, ['issueDate', 'expiryDate']);
  }

  // Employees: optional date/number fields arrive as '' when left blank in the form
  if (collectionType === E.EMPLOYEES) {
    nullEmptyDates(cleaned, [
      'cardExpiryDate', 'birthDate', 'hireDate', 'seamanBookIssueDate',
      'seamanBookExpiry', 'marinePassportExpiry',
    ]);
    ['salary', 'dailyRate', 'amount'].forEach((f) => {
      if (cleaned[f] === '' || cleaned[f] === undefined) cleaned[f] = null;
      else if (cleaned[f] !== null) cleaned[f] = Number(cleaned[f]);
    });
  }

  // Flatten finance report dateRange
  if (collectionType === E.FINANCE_REPORTS) {
    if (cleaned.dateRange && typeof cleaned.dateRange === 'object') {
      cleaned.dateRangeFrom = cleaned.dateRange.from;
      cleaned.dateRangeTo = cleaned.dateRange.to;
      delete cleaned.dateRange;
    }
  }

  // Flatten permit letter createdBy → letterCreatedBy
  if (collectionType === E.PERMIT_LETTERS) {
    if (cleaned.createdBy !== undefined) {
      cleaned.letterCreatedBy = cleaned.createdBy;
      delete cleaned.createdBy;
    }
  }
};
